import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { findAgentManagedExtension, isAgentManagedExtension } from '../../common/utils';

import { setMeta } from './state';

interface AutoOpenView {
	update: () => void;
}

const collectLocalIds = (doc: PMNode): Set<string> => {
	const found = new Set<string>();
	doc.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}
		const localId = node.attrs.localId as string | undefined;
		if (localId) {
			found.add(localId);
		}
		return false;
	});
	return found;
};

const hasNewLocalId = (prev: Set<string>, next: Set<string>): boolean => {
	for (const id of next) {
		if (!prev.has(id)) {
			return true;
		}
	}
	return false;
};

/**
 * Watches for newly-inserted agent-managed extensions and dispatches
 * `OPEN_CONFIG` when the cursor lands inside the new block. Only
 * triggers for genuine user insertions — not on initial document load,
 * document replacement, or remote/collab operations.
 *
 * Returns the `view` object expected by the ProseMirror plugin spec.
 */
export const createAutoOpenConfigOnInsertView = (editorView: EditorView): AutoOpenView => {
	// Initialize to `null` so the first `update()` call seeds the
	// baseline without treating pre-existing blocks as new inserts.
	let seenLocalIds: Set<string> | null = null;
	return {
		update() {
			const nextSeen = collectLocalIds(editorView.state.doc);

			if (seenLocalIds === null) {
				// On first update, seed the baseline from the current document; which may already contain
				// blocks loaded from the server, and exit without auto-opening.
				seenLocalIds = nextSeen;
				return;
			}

			const sawNewBlock = hasNewLocalId(seenLocalIds, nextSeen);
			seenLocalIds = nextSeen;
			if (!sawNewBlock) {
				return;
			}
			if (!findAgentManagedExtension(editorView.state)) {
				return;
			}
			editorView.dispatch(setMeta(editorView.state.tr, { type: 'OPEN_CONFIG' }));
		},
	};
};
